/**
 * Visit Notes Controller (Driver Adapter)
 *
 * Interface layer between UI and business logic
 */

import type {
  CustomAssessmentCategory,
  IVisitNotesController,
  IVisitNotesService,
  ICreateVisitNoteRequest,
  IUpdateVisitNoteRequest,
  ICreateVisitNoteResponse,
  IUpdateVisitNoteResponse,
  IGetAllVisitNotesResponse,
  IGetVisitNoteProvidersResponse,
  ISummarizeNarrativeRequest,
  ISummarizeNarrativeResponse,
  ISendEfaxRequest,
  ISendEfaxResponse,
  IDownloadPartnershipSummaryDocxRequest,
  IDownloadPartnershipSummaryDocxResponse,
  IGetSidePeekProfileResponse,
  IGenerateSidePeekProfileResponse,
  IUpdateSidePeekProfileResponse,
  IGetSafetyNoteResponse,
  IUpdateSafetyNoteResponse,
  IGetCodeStatusResponse,
  IUpdateCodeStatusResponse,
  CodeStatusValue,
  IGetPatientContactsResponse,
  IUpdatePatientContactsResponse,
  IUpdatePatientContactsRequest,
  IGetPatientExtraInfoResponse,
  IGetPatientPrimaryLanguageResponse,
  IUpdatePatientPrimaryLanguageResponse,
  IUpdatePatientTimezoneResponse,
  IGetNextVisitPlanResponse,
  IUpdateNextVisitPlanResponse,
  INextVisitPlan,
  IGetPatientSummaryResponse,
  IGetPatientSummaryGenerationStatusResponse,
  IGeneratePatientSummaryResponse,
  IGetNoteLlmOutputResponse,
  IClearManualEditsHistoryResponse,
  VisitNoteConsentType,
  IGetConsentTemplateMetadataResponse,
  IGetHhRpmConsentDraftMetadataResponse,
  IGetConsentTemplatePageResponse,
  IHhRpmConsentRequest,
  IHhRpmPatientConsentRequest,
  ISubmitHhRpmConsentResponse,
  IGetPatientConsentHistoryResponse,
  IGetClinicianAcuityCatalogResponse,
  IClinicianAssessmentUpsert,
  IPutClinicianAssessmentResponse,
  IRefreshPastContextPrechartResponse,
  ICarePlanPrechartResponse,
  IUpdateCarePlanPrechartRequest,
  IDeleteCarePlanPrechartRequest,
  IGetPatientCaregiversResponse,
  IPatientCaregiverResponse,
  IDeletePatientCaregiverResponse,
  ICreatePatientCaregiverRequest,
  IUpdatePatientCaregiverRequest,
  IPatientPrimaryCareProviderResponse,
  ICreatePatientPrimaryCareProviderRequest,
  IUpdatePatientPrimaryCareProviderRequest,
  IGetScheduledVisitAssignmentsResponse,
  IPatientSpecialistsResponse,
  IPatientSpecialistResponse,
  IDeletePatientSpecialistResponse,
  ICreatePatientSpecialistRequest,
  IUpdatePatientSpecialistRequest,
  InsuranceCardSide,
  IPatientInsuranceCardResponse,
  ICreateCustomAssessmentTemplateRequest,
  IDeleteCustomAssessmentFormResponse,
  ICustomAssessmentFormResponse,
  ICustomAssessmentFormsEnvelope,
  ICustomAssessmentResult,
  ICustomAssessmentTemplate,
  IPutCustomAssessmentFormRequest,
  IUpdateCustomAssessmentTemplateRequest,
} from "../../core/ports/driver/VisitNotesService";

export const VisitNotesControllerFactory = ({
  visitNotesService,
}: {
  visitNotesService: IVisitNotesService;
}): IVisitNotesController => {
  const addPendingCustomTodoIdToCreateRequest = async (
    request: ICreateVisitNoteRequest
  ): Promise<ICreateVisitNoteRequest> => {
    if (request.doc_type !== "custom_visit" || request.custom_todo_id) {
      return request;
    }

    try {
      const { getPendingCustomTodoLinkFor } = await import(
        "../../../util/navigatorCustomTodoHandoff"
      );
      const pending = getPendingCustomTodoLinkFor({
        patientId: request.patient_id,
        clinicId: request.clinic_id,
      });

      return pending
        ? { ...request, custom_todo_id: pending.customTodoId }
        : request;
    } catch (err) {
      console.warn(
        "[VisitNotesController] Failed to attach pending custom todo id:",
        err
      );
      return request;
    }
  };

  const linkPendingCustomTodoToNote = async (
    request: ICreateVisitNoteRequest,
    noteId: number
  ): Promise<void> => {
    if (request.doc_type !== "custom_visit") return;

    const {
      CUSTOM_TODO_LINK_RETRY_DELAYS_MS,
      clearPendingCustomTodoLink,
      getPendingCustomTodoLinkFor,
      waitForCustomTodoLinkRetry,
    } = await import("../../../util/navigatorCustomTodoHandoff");
    const pending = getPendingCustomTodoLinkFor({
      patientId: request.patient_id,
      clinicId: request.clinic_id,
    });

    if (!pending) return;

    // The note was created for a different todo than the stored handoff (the
    // user picked another todo after abandoning a Navigator Start). The BE
    // already linked the note's own todo on create; linking the stale one too
    // would put two todos on one draft. Drop the stale handoff instead.
    if (
      request.custom_todo_id != null &&
      request.custom_todo_id !== pending.customTodoId
    ) {
      clearPendingCustomTodoLink({
        customTodoId: pending.customTodoId,
        patientId: pending.patientId,
        clinicId: pending.clinicId,
      });
      return;
    }

    // Dynamic import avoids a static cycle since both controllers are
    // re-exported from AppController.ts.
    const { navigatorController } = await import("../../../AppController");
    let lastError: unknown;

    for (
      let attempt = 0;
      attempt < CUSTOM_TODO_LINK_RETRY_DELAYS_MS.length;
      attempt += 1
    ) {
      const delay = CUSTOM_TODO_LINK_RETRY_DELAYS_MS[attempt];
      if (delay > 0) await waitForCustomTodoLinkRetry(delay);

      try {
        const linkResult = await navigatorController.updateCustomTodo({
          clinicId: request.clinic_id,
          patientId: request.patient_id,
          todoId: pending.customTodoId,
          ipvNoteId: noteId,
        });

        if (linkResult.success) {
          clearPendingCustomTodoLink({
            customTodoId: pending.customTodoId,
            patientId: pending.patientId,
            clinicId: pending.clinicId,
          });
          return;
        }

        lastError = linkResult.error?.message || "Unknown link failure";
      } catch (err) {
        lastError = err;
      }

      console.warn("[VisitNotesController] Custom todo link attempt failed:", {
        attempt: attempt + 1,
        customTodoId: pending.customTodoId,
        noteId,
        patientId: request.patient_id,
        clinicId: request.clinic_id,
        error: lastError,
      });
    }

    console.warn(
      "[VisitNotesController] Custom todo link failed after retries; keeping pending handoff:",
      {
        customTodoId: pending.customTodoId,
        noteId,
        patientId: request.patient_id,
        clinicId: request.clinic_id,
        error: lastError,
      }
    );
  };

  /**
   * Create a new visit note (draft or completed).
   *
   * Side effect: if Navigator stashed a "pending custom todo link" for this
   * exact patient + clinic before navigating to the editor, PATCH the new
   * note id onto the custom todo so the BE flips its state to in_progress.
   * Failures here are swallowed after retries — they should never block the
   * editor from opening the new draft, but the pending handoff is kept so we
   * can diagnose/retry instead of silently dropping the link intent.
   */
  const createVisitNote = async (
    request: ICreateVisitNoteRequest
  ): Promise<ICreateVisitNoteResponse> => {
    const createRequest = await addPendingCustomTodoIdToCreateRequest(request);
    const result = await visitNotesService.createVisitNote(createRequest);

    if (result.success && result.data?.id) {
      try {
        await linkPendingCustomTodoToNote(createRequest, result.data.id);
      } catch (err) {
        console.warn(
          "[VisitNotesController] Failed to link custom todo to new note:",
          err
        );
      }
    }

    return result;
  };

  /**
   * Update/auto-save a visit note
   */
  const updateVisitNote = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IUpdateVisitNoteRequest
  ): Promise<IUpdateVisitNoteResponse> => {
    return await visitNotesService.updateVisitNote(
      clinicId,
      patientId,
      noteId,
      request
    );
  };

  /**
   * Get all visit notes for a clinic (current-clinic scoping; mobile clients).
   */
  const getAllVisitNotes = async (
    clinicId: number,
    params?: {
      monthOffset?: number;
      daysBack?: number;
      daysForward?: number;
      providerEmail?: string;
      providerName?: string;
      includeEmpty?: boolean;
      skipHealthscribe?: boolean;
      circadia?: boolean;
    }
  ): Promise<IGetAllVisitNotesResponse> => {
    return await visitNotesService.getAllVisitNotes(clinicId, params);
  };

  /**
   * Get all visit notes for a clinic (admission-scoped variant — web list view).
   * Surfaces transferred patients' historical notes under the old clinic.
   */
  const getAllVisitNotesFull = async (
    clinicId: number,
    params?: {
      monthOffset?: number;
      daysBack?: number;
      daysForward?: number;
      providerEmail?: string;
      providerName?: string;
      includeEmpty?: boolean;
      skipHealthscribe?: boolean;
      circadia?: boolean;
    }
  ): Promise<IGetAllVisitNotesResponse> => {
    return await visitNotesService.getAllVisitNotesFull(clinicId, params);
  };

  const getScheduledVisitAssignments = async (
    clinicId: number,
    daysForward?: number
  ): Promise<IGetScheduledVisitAssignmentsResponse> =>
    visitNotesService.getScheduledVisitAssignments(clinicId, daysForward);

  /**
   * Get distinct providers for the visit notes provider filter dropdown
   */
  const getVisitNoteProviders = async (
    clinicId: number,
    params?: { circadia?: boolean }
  ): Promise<IGetVisitNoteProvidersResponse> => {
    return await visitNotesService.getVisitNoteProviders(clinicId, params);
  };

  /**
   * Server-side search for visit notes (transcript search only).
   * Empty query returns all third_party_np_visit notes in date range.
   */
  const searchVisitNotes = async (
    clinicId: number,
    searchQuery?: string,
    limit?: number
  ): Promise<IGetAllVisitNotesResponse> => {
    return await visitNotesService.searchVisitNotes(
      clinicId,
      searchQuery,
      limit
    );
  };

  /**
   * Get archived visit notes for a clinic
   */
  const getArchivedVisitNotes = async (
    clinicId: number,
    params?: { circadia?: boolean }
  ): Promise<IGetAllVisitNotesResponse> => {
    return await visitNotesService.getArchivedVisitNotes(clinicId, params);
  };

  /**
   * Restore an archived visit note
   */
  const restoreArchivedVisitNote = async (
    clinicId: number,
    ipvNoteId: number
  ) => {
    return await visitNotesService.restoreArchivedVisitNote(
      clinicId,
      ipvNoteId
    );
  };

  /**
   * Get a single visit note
   */
  const getVisitNote = async (
    noteId: number
  ): Promise<{ success: boolean; data?: any; error?: any }> => {
    return await visitNotesService.getVisitNote(noteId);
  };

  const getClinicianAcuityCatalog =
    async (): Promise<IGetClinicianAcuityCatalogResponse> =>
      visitNotesService.getClinicianAcuityCatalog();

  const putClinicianAssessment = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IClinicianAssessmentUpsert
  ): Promise<IPutClinicianAssessmentResponse> =>
    visitNotesService.putClinicianAssessment(
      clinicId,
      patientId,
      noteId,
      request
    );

  const listCustomAssessmentTemplates = (
    clinicId: number,
    category: CustomAssessmentCategory,
    includeArchived?: boolean
  ): Promise<ICustomAssessmentResult<ICustomAssessmentTemplate[]>> =>
    visitNotesService.listCustomAssessmentTemplates(
      clinicId,
      category,
      includeArchived
    );

  const getCustomAssessmentTemplate = (
    clinicId: number,
    templateId: number
  ): Promise<ICustomAssessmentResult<ICustomAssessmentTemplate>> =>
    visitNotesService.getCustomAssessmentTemplate(clinicId, templateId);

  const createCustomAssessmentTemplate = (
    clinicId: number,
    request: ICreateCustomAssessmentTemplateRequest
  ): Promise<ICustomAssessmentResult<ICustomAssessmentTemplate>> =>
    visitNotesService.createCustomAssessmentTemplate(clinicId, request);

  const updateCustomAssessmentTemplate = (
    clinicId: number,
    templateId: number,
    request: IUpdateCustomAssessmentTemplateRequest
  ): Promise<ICustomAssessmentResult<ICustomAssessmentTemplate>> =>
    visitNotesService.updateCustomAssessmentTemplate(
      clinicId,
      templateId,
      request
    );

  const archiveCustomAssessmentTemplate = (
    clinicId: number,
    templateId: number,
    baseRevision: number
  ): Promise<ICustomAssessmentResult<ICustomAssessmentTemplate>> =>
    visitNotesService.archiveCustomAssessmentTemplate(
      clinicId,
      templateId,
      baseRevision
    );

  const publishCustomAssessmentTemplate = (
    clinicId: number,
    templateId: number,
    baseRevision: number
  ): Promise<ICustomAssessmentResult<ICustomAssessmentTemplate>> =>
    visitNotesService.publishCustomAssessmentTemplate(
      clinicId,
      templateId,
      baseRevision
    );

  const getCustomAssessmentForms = (
    clinicId: number,
    patientId: number,
    noteId: number
  ): Promise<ICustomAssessmentResult<ICustomAssessmentFormsEnvelope>> =>
    visitNotesService.getCustomAssessmentForms(clinicId, patientId, noteId);

  const putCustomAssessmentForm = (
    clinicId: number,
    patientId: number,
    noteId: number,
    templateId: number,
    request: IPutCustomAssessmentFormRequest
  ): Promise<ICustomAssessmentResult<ICustomAssessmentFormResponse>> =>
    visitNotesService.putCustomAssessmentForm(
      clinicId,
      patientId,
      noteId,
      templateId,
      request
    );

  const deleteCustomAssessmentForm = (
    clinicId: number,
    patientId: number,
    noteId: number,
    templateId: number,
    baseRevision: number
  ): Promise<ICustomAssessmentResult<IDeleteCustomAssessmentFormResponse>> =>
    visitNotesService.deleteCustomAssessmentForm(
      clinicId,
      patientId,
      noteId,
      templateId,
      baseRevision
    );

  const refreshPastContextPrechart = async (
    clinicId: number,
    patientId: number,
    noteId: number
  ): Promise<IRefreshPastContextPrechartResponse> =>
    visitNotesService.refreshPastContextPrechart(clinicId, patientId, noteId);

  const getCarePlanPrechart = async (
    clinicId: number,
    patientId: number,
    noteId: number
  ): Promise<ICarePlanPrechartResponse> =>
    visitNotesService.getCarePlanPrechart(clinicId, patientId, noteId);

  const updateCarePlanPrechart = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IUpdateCarePlanPrechartRequest
  ): Promise<ICarePlanPrechartResponse> =>
    visitNotesService.updateCarePlanPrechart(
      clinicId,
      patientId,
      noteId,
      request
    );

  const refreshCarePlanPrechart = async (
    clinicId: number,
    patientId: number,
    noteId: number
  ): Promise<ICarePlanPrechartResponse> =>
    visitNotesService.refreshCarePlanPrechart(clinicId, patientId, noteId);

  const deleteCarePlanPrechart = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IDeleteCarePlanPrechartRequest
  ): Promise<ICarePlanPrechartResponse> =>
    visitNotesService.deleteCarePlanPrechart(
      clinicId,
      patientId,
      noteId,
      request
    );

  /**
   * Summarize a narrative for sharing as PDF
   */
  const summarizeNarrative = async (
    request: ISummarizeNarrativeRequest
  ): Promise<ISummarizeNarrativeResponse> => {
    return await visitNotesService.summarizeNarrative(request);
  };

  /**
   * Send visit note PDF via eFax
   */
  const sendEfax = async (
    request: ISendEfaxRequest
  ): Promise<ISendEfaxResponse> => {
    return await visitNotesService.sendEfax(request);
  };

  /**
   * Generate and download Compassus partnership summary DOCX
   */
  const downloadPartnershipSummaryDocx = async (
    request: IDownloadPartnershipSummaryDocxRequest
  ): Promise<IDownloadPartnershipSummaryDocxResponse> => {
    return await visitNotesService.downloadPartnershipSummaryDocx(request);
  };

  /**
   * Get Side Peek Profile for a patient
   */
  const getSidePeekProfile = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetSidePeekProfileResponse> => {
    return await visitNotesService.getSidePeekProfile(clinicId, patientId);
  };

  /**
   * Trigger Side Peek Profile generation for a patient
   */
  const generateSidePeekProfile = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    section?: "diagnoses" | "medications" | "allergies",
    model?: string
  ): Promise<IGenerateSidePeekProfileResponse> => {
    return await visitNotesService.generateSidePeekProfile(
      clinicId,
      patientId,
      noteId,
      section,
      model
    );
  };

  /**
   * Update Side Peek Profile for a patient (manual edits)
   */
  const updateSidePeekProfile = async (
    clinicId: number,
    patientId: number,
    updateData: {
      diagnoses?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        icd10_code?: string | null;
        is_primary?: boolean | null;
        onset_date?: string | null;
      }>;
      medications?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        status?: string | null;
      }>;
      allergies?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        icd10_code?: string | null;
        status?: string | null;
        allergen?: string | null;
        reaction?: string | null;
        severity?: string | null;
        onset_date?: string | null;
        is_primary?: boolean | null;
      }>;
    }
  ): Promise<IUpdateSidePeekProfileResponse> => {
    return await visitNotesService.updateSidePeekProfile(
      clinicId,
      patientId,
      updateData
    );
  };

  /**
   * Get Safety Note for a patient
   */
  const getSafetyNote = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetSafetyNoteResponse> => {
    return await visitNotesService.getSafetyNote(clinicId, patientId);
  };

  /**
   * Update Safety Note for a patient
   */
  const updateSafetyNote = async (
    clinicId: number,
    patientId: number,
    content: string
  ): Promise<IUpdateSafetyNoteResponse> => {
    return await visitNotesService.updateSafetyNote(
      clinicId,
      patientId,
      content
    );
  };

  /**
   * Get Code Status for a patient
   */
  const getCodeStatus = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetCodeStatusResponse> => {
    return await visitNotesService.getCodeStatus(clinicId, patientId);
  };

  /**
   * Update Code Status for a patient
   */
  const updateCodeStatus = async (
    clinicId: number,
    patientId: number,
    status: CodeStatusValue,
    otherStatus?: string | null
  ): Promise<IUpdateCodeStatusResponse> => {
    return await visitNotesService.updateCodeStatus(
      clinicId,
      patientId,
      status,
      otherStatus
    );
  };

  /**
   * Get Patient Contacts
   */
  const getPatientContacts = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientContactsResponse> => {
    return await visitNotesService.getPatientContacts(clinicId, patientId);
  };

  /**
   * Add or remove a patient contact
   */
  const updatePatientContacts = async (
    clinicId: number,
    patientId: number,
    request: IUpdatePatientContactsRequest
  ): Promise<IUpdatePatientContactsResponse> => {
    return await visitNotesService.updatePatientContacts(
      clinicId,
      patientId,
      request
    );
  };

  const getPatientCaregivers = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientCaregiversResponse> =>
    visitNotesService.getPatientCaregivers(clinicId, patientId);

  const createPatientCaregiver = async (
    clinicId: number,
    patientId: number,
    request: ICreatePatientCaregiverRequest
  ): Promise<IPatientCaregiverResponse> =>
    visitNotesService.createPatientCaregiver(clinicId, patientId, request);

  const updatePatientCaregiver = async (
    clinicId: number,
    patientId: number,
    caregiverId: string,
    request: IUpdatePatientCaregiverRequest
  ): Promise<IPatientCaregiverResponse> =>
    visitNotesService.updatePatientCaregiver(
      clinicId,
      patientId,
      caregiverId,
      request
    );

  const deletePatientCaregiver = async (
    clinicId: number,
    patientId: number,
    caregiverId: string
  ): Promise<IDeletePatientCaregiverResponse> =>
    visitNotesService.deletePatientCaregiver(clinicId, patientId, caregiverId);

  const getPatientPrimaryCareProvider = async (
    clinicId: number,
    patientId: number
  ): Promise<IPatientPrimaryCareProviderResponse> =>
    visitNotesService.getPatientPrimaryCareProvider(clinicId, patientId);

  const createPatientPrimaryCareProvider = async (
    clinicId: number,
    patientId: number,
    request: ICreatePatientPrimaryCareProviderRequest
  ): Promise<IPatientPrimaryCareProviderResponse> =>
    visitNotesService.createPatientPrimaryCareProvider(
      clinicId,
      patientId,
      request
    );

  const updatePatientPrimaryCareProvider = async (
    clinicId: number,
    patientId: number,
    request: IUpdatePatientPrimaryCareProviderRequest
  ): Promise<IPatientPrimaryCareProviderResponse> =>
    visitNotesService.updatePatientPrimaryCareProvider(
      clinicId,
      patientId,
      request
    );

  const getPatientSpecialists = async (
    clinicId: number,
    patientId: number
  ): Promise<IPatientSpecialistsResponse> =>
    visitNotesService.getPatientSpecialists(clinicId, patientId);

  const createPatientSpecialist = async (
    clinicId: number,
    patientId: number,
    request: ICreatePatientSpecialistRequest
  ): Promise<IPatientSpecialistResponse> =>
    visitNotesService.createPatientSpecialist(clinicId, patientId, request);

  const updatePatientSpecialist = async (
    clinicId: number,
    patientId: number,
    specialistId: number,
    request: IUpdatePatientSpecialistRequest
  ): Promise<IPatientSpecialistResponse> =>
    visitNotesService.updatePatientSpecialist(
      clinicId,
      patientId,
      specialistId,
      request
    );

  const deletePatientSpecialist = async (
    clinicId: number,
    patientId: number,
    specialistId: number
  ): Promise<IDeletePatientSpecialistResponse> =>
    visitNotesService.deletePatientSpecialist(
      clinicId,
      patientId,
      specialistId
    );

  const getPatientInsuranceCard = async (
    clinicId: number,
    patientId: number
  ): Promise<IPatientInsuranceCardResponse> =>
    visitNotesService.getPatientInsuranceCard(clinicId, patientId);

  const uploadPatientInsuranceCardImage = async (
    clinicId: number,
    patientId: number,
    side: InsuranceCardSide,
    file: File
  ): Promise<IPatientInsuranceCardResponse> =>
    visitNotesService.uploadPatientInsuranceCardImage(
      clinicId,
      patientId,
      side,
      file
    );

  const deletePatientInsuranceCardImage = async (
    clinicId: number,
    patientId: number,
    side: InsuranceCardSide
  ): Promise<IPatientInsuranceCardResponse> =>
    visitNotesService.deletePatientInsuranceCardImage(
      clinicId,
      patientId,
      side
    );

  const getPatientExtraInfo = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientExtraInfoResponse> => {
    return await visitNotesService.getPatientExtraInfo(clinicId, patientId);
  };

  const getPatientPrimaryLanguage = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientPrimaryLanguageResponse> => {
    return await visitNotesService.getPatientPrimaryLanguage(
      clinicId,
      patientId
    );
  };

  const updatePatientPrimaryLanguage = async (
    clinicId: number,
    patientId: number,
    primaryLanguage: string | null
  ): Promise<IUpdatePatientPrimaryLanguageResponse> => {
    return await visitNotesService.updatePatientPrimaryLanguage(
      clinicId,
      patientId,
      primaryLanguage
    );
  };

  const updatePatientTimezone = async (
    clinicId: number,
    patientId: number,
    patientTimezone: string | null
  ): Promise<IUpdatePatientTimezoneResponse> => {
    return await visitNotesService.updatePatientTimezone(
      clinicId,
      patientId,
      patientTimezone
    );
  };

  /**
   * Get Next Visit Plan for a patient
   */
  const getNextVisitPlan = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetNextVisitPlanResponse> => {
    return await visitNotesService.getNextVisitPlan(clinicId, patientId);
  };

  /**
   * Update Next Visit Plan for a patient
   */
  const updateNextVisitPlan = async (
    clinicId: number,
    patientId: number,
    plan: INextVisitPlan["plan"]
  ): Promise<IUpdateNextVisitPlanResponse> => {
    return await visitNotesService.updateNextVisitPlan(
      clinicId,
      patientId,
      plan
    );
  };

  const getPatientSummary = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientSummaryResponse> => {
    return await visitNotesService.getPatientSummary(clinicId, patientId);
  };

  const getPatientSummaryGenerationStatus = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientSummaryGenerationStatusResponse> => {
    return await visitNotesService.getPatientSummaryGenerationStatus(
      clinicId,
      patientId
    );
  };

  const generatePatientSummary = async (
    clinicId: number,
    patientId: number
  ): Promise<IGeneratePatientSummaryResponse> => {
    return await visitNotesService.generatePatientSummary(clinicId, patientId);
  };

  /**
   * Get latest LLM output for a note
   */
  const getNoteLlmOutput = async (
    noteId: number,
    clinicId: number
  ): Promise<IGetNoteLlmOutputResponse> => {
    return await visitNotesService.getNoteLlmOutput(noteId, clinicId);
  };

  /**
   * Clear manual edits history for a note
   */
  const clearManualEditsHistory = async (
    noteId: number,
    clinicId: number
  ): Promise<IClearManualEditsHistoryResponse> => {
    return await visitNotesService.clearManualEditsHistory(noteId, clinicId);
  };

  const getConsentTemplateMetadata = async (
    consentType: VisitNoteConsentType
  ): Promise<IGetConsentTemplateMetadataResponse> =>
    visitNotesService.getConsentTemplateMetadata(consentType);

  const getHhRpmConsentDraftMetadata = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetHhRpmConsentDraftMetadataResponse> =>
    visitNotesService.getHhRpmConsentDraftMetadata(clinicId, patientId);

  const getConsentTemplatePageImage = async (
    consentType: VisitNoteConsentType,
    page: number,
    dpi?: number
  ): Promise<IGetConsentTemplatePageResponse> =>
    visitNotesService.getConsentTemplatePageImage(consentType, page, dpi);

  const submitHhRpmConsent = async (
    clinicId: number,
    patientId: number,
    request: IHhRpmConsentRequest
  ): Promise<ISubmitHhRpmConsentResponse> =>
    visitNotesService.submitHhRpmConsent(clinicId, patientId, request);

  const getHhRpmPatientConsentDraftMetadata = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetHhRpmConsentDraftMetadataResponse> =>
    visitNotesService.getHhRpmPatientConsentDraftMetadata(clinicId, patientId);

  const submitHhRpmPatientConsent = async (
    clinicId: number,
    patientId: number,
    request: IHhRpmPatientConsentRequest
  ): Promise<ISubmitHhRpmConsentResponse> =>
    visitNotesService.submitHhRpmPatientConsent(clinicId, patientId, request);

  const getPatientConsentHistory = async (
    clinicId: number,
    patientId: number,
    patientScope = false
  ): Promise<IGetPatientConsentHistoryResponse> =>
    visitNotesService.getPatientConsentHistory(
      clinicId,
      patientId,
      patientScope
    );

  return {
    createVisitNote,
    updateVisitNote,
    getAllVisitNotes,
    getAllVisitNotesFull,
    getScheduledVisitAssignments,
    getVisitNoteProviders,
    searchVisitNotes,
    getArchivedVisitNotes,
    restoreArchivedVisitNote,
    getVisitNote,
    getClinicianAcuityCatalog,
    putClinicianAssessment,
    listCustomAssessmentTemplates,
    getCustomAssessmentTemplate,
    createCustomAssessmentTemplate,
    updateCustomAssessmentTemplate,
    archiveCustomAssessmentTemplate,
    publishCustomAssessmentTemplate,
    getCustomAssessmentForms,
    putCustomAssessmentForm,
    deleteCustomAssessmentForm,
    refreshPastContextPrechart,
    getCarePlanPrechart,
    updateCarePlanPrechart,
    refreshCarePlanPrechart,
    deleteCarePlanPrechart,
    summarizeNarrative,
    sendEfax,
    downloadPartnershipSummaryDocx,
    getSidePeekProfile,
    generateSidePeekProfile,
    updateSidePeekProfile,
    getSafetyNote,
    updateSafetyNote,
    getCodeStatus,
    updateCodeStatus,
    getPatientContacts,
    updatePatientContacts,
    getPatientCaregivers,
    createPatientCaregiver,
    updatePatientCaregiver,
    deletePatientCaregiver,
    getPatientPrimaryCareProvider,
    createPatientPrimaryCareProvider,
    updatePatientPrimaryCareProvider,
    getPatientSpecialists,
    createPatientSpecialist,
    updatePatientSpecialist,
    deletePatientSpecialist,
    getPatientInsuranceCard,
    uploadPatientInsuranceCardImage,
    deletePatientInsuranceCardImage,
    getPatientExtraInfo,
    getPatientPrimaryLanguage,
    updatePatientPrimaryLanguage,
    updatePatientTimezone,
    getNextVisitPlan,
    updateNextVisitPlan,
    getPatientSummary,
    getPatientSummaryGenerationStatus,
    generatePatientSummary,
    getNoteLlmOutput,
    clearManualEditsHistory,
    getConsentTemplateMetadata,
    getHhRpmConsentDraftMetadata,
    getHhRpmPatientConsentDraftMetadata,
    getConsentTemplatePageImage,
    submitHhRpmConsent,
    submitHhRpmPatientConsent,
    getPatientConsentHistory,
  };
};

export type IVisitNotesControllerType = ReturnType<
  typeof VisitNotesControllerFactory
>;
